import { type SelectionState, type SelectionRange } from '../../../machine/state/types';

import { getCellElementByIndex } from './get-cell-element-by-index';
import { isSingleCellSelection } from './is-single-cell-selection';

export function getBoxGeometry(
  range: SelectionRange,
  scrollableContainer: HTMLElement,
  mode: SelectionState['mode']
) {
  // If this is a single cell selection, we don't have any geometry
  // because we're not showing the highlight.
  if (isSingleCellSelection(range, mode)) {
    return false;
  }

  // Resolve the Corners
  const minRowCoord =
    range.anchor.row.index < range.lead.row.index ? range.anchor.row : range.lead.row;
  const maxRowCoord =
    range.anchor.row.index > range.lead.row.index ? range.anchor.row : range.lead.row;
  const minColCoord =
    range.anchor.col.index < range.lead.col.index ? range.anchor.col : range.lead.col;
  const maxColCoord =
    range.anchor.col.index > range.lead.col.index ? range.anchor.col : range.lead.col;

  // Find Elements
  // We use the coords to find *any* valid cell in that row/col to get measurements
  const topLeftEl = getCellElementByIndex({
    rowIndex: minRowCoord.index,
    colIndex: minColCoord.index,
  });

  const bottomRightEl = getCellElementByIndex({
    rowIndex: maxRowCoord.index,
    colIndex: maxColCoord.index,
  });

  if (!topLeftEl || !bottomRightEl) {
    return null;
  }

  // Get Viewport Coordinates
  const topLeftRect = topLeftEl.getBoundingClientRect();
  const bottomRightRect = bottomRightEl.getBoundingClientRect();
  const containerRect = scrollableContainer.getBoundingClientRect();

  // Calculate Base Dimensions (Cell Mode)
  let x = topLeftRect.left - containerRect.left + scrollableContainer.scrollLeft;
  let y = topLeftRect.top - containerRect.top + scrollableContainer.scrollTop;
  let width = bottomRightRect.right - topLeftRect.left;
  let height = bottomRightRect.bottom - topLeftRect.top;

  // Apply Mode Overrides
  if (mode === 'row') {
    x = 0;
    // Full width
    // Y and Height are preserved from the cell math
    width = scrollableContainer.scrollWidth;
  }

  if (mode === 'column') {
    y = 0;
    // Full height
    // X and Width are preserved from the cell math
    height = scrollableContainer.scrollHeight;
  }

  return { x, y, width, height };
}
